import { writable, get } from 'svelte/store';
// Import the rates JS module. Ensure the path is correct relative to this file.
// Adjust the path if necessary, e.g. '../../config/providerRates.js' if running from src/services
// Assuming this runs server-side or the JS module is bundled/accessible client-side if needed there.
// For server-side usage, this path needs to be relative to that file or absolute.
import providerRates from '../../config/providerRates.js';

// Remove old constants, rates are now dynamic
// const INPUT_COST_PER_MILLION = 0.150;
// const OUTPUT_COST_PER_MILLION = 0.600;

export const totalInputTokens = writable(0);
export const totalOutputTokens = writable(0);
export const totalCost = writable(0);

/**
 * Tracks the cost of a query based on token usage.
 */
export class QueryCostTracker {
  constructor() {
    // Store costs per provider:model string key
    this.costs = {};
    this.totalCost = 0;
    this.totalInputTokens = 0;
    this.totalOutputTokens = 0;
  }

  /**
   * Adds cost for a specific LLM call, categorized by type and provider/model.
   * @param {string} type - The type of operation (e.g., 'enhance', 'title', 'answer').
   * @param {string} providerModel - The provider and model key (e.g., 'openai:gpt-4o-mini').
   * @param {number} inputTokens - Number of input tokens used.
   * @param {number} outputTokens - Number of output tokens generated.
   * @returns {object} The updated costs object for this tracker instance.
   */
  addCost(type, providerModel, inputTokens, outputTokens) {
    const costResult = calculateCost(inputTokens, outputTokens, providerModel);

    if (!this.costs[type]) {
      this.costs[type] = { inputTokens: 0, outputTokens: 0, cost: 0, details: {} };
    }

    // Store details per providerModel under the type
    if (!this.costs[type].details[providerModel]) {
        this.costs[type].details[providerModel] = { inputTokens: 0, outputTokens: 0, cost: 0 };
    }

    this.costs[type].details[providerModel].inputTokens += inputTokens;
    this.costs[type].details[providerModel].outputTokens += outputTokens;
    this.costs[type].details[providerModel].cost += costResult.cost;

    // Update totals for the type
    this.costs[type].inputTokens += inputTokens;
    this.costs[type].outputTokens += outputTokens;
    this.costs[type].cost += costResult.cost;

    // Update overall totals for the instance
    this.totalInputTokens += inputTokens;
    this.totalOutputTokens += outputTokens;
    this.totalCost += costResult.cost;

    // Update global stores (if this tracker is meant to be the single source of truth)
    totalInputTokens.set(this.totalInputTokens);
    totalOutputTokens.set(this.totalOutputTokens);
    totalCost.set(this.totalCost);

    return this.costs;
  }

  /**
   * Gets the total accumulated costs tracked by this instance.
   * @returns {object} An object containing total tokens and cost.
   */
  getTotalCost() {
    return {
        totalInputTokens: this.totalInputTokens,
        totalOutputTokens: this.totalOutputTokens,
        totalCost: this.totalCost,
        breakdown: this.costs // Include the detailed breakdown
    };
  }
}

/**
 * Calculates the cost of an LLM call based on input/output tokens and provider/model rates.
 * @param {number} inputTokens - Number of input tokens.
 * @param {number} outputTokens - Number of output tokens.
 * @param {string} providerModel - The provider and model key (e.g., 'openai:gpt-4o-mini').
 * @returns {{inputTokens: number, outputTokens: number, cost: number, rateUsed: object|null}} Cost calculation result.
 */
export function calculateCost(inputTokens, outputTokens, providerModel = 'unknown') {
  const rates = providerRates[providerModel] || providerRates['unknown'];

  if (!rates) {
    console.warn(`[CostTracker] No rates found for providerModel: ${providerModel}. Using default 0 cost.`);
    return {
      inputTokens,
      outputTokens,
      cost: 0,
      rateUsed: null
    };
  }

  const inputCost = (inputTokens / 1000000) * (rates.input || 0);
  const outputCost = (outputTokens / 1000000) * (rates.output || 0);
  const totalCallCost = inputCost + outputCost;

  // console.log(`[CostTracker] Calculated cost for ${providerModel}: In=${inputTokens}, Out=${outputTokens}, Cost=$${totalCallCost.toFixed(6)} (Rates: In=$${rates.input}/M, Out=$${rates.output}/M)`);

  return {
    inputTokens,
    outputTokens,
    cost: totalCallCost,
    rateUsed: rates
  };
}

/**
 * Logs the total cost based on provided usage data.
 * @param {{inputTokens: number, outputTokens: number}} usageData - Object containing token counts.
 * @param {string} providerModel - The provider and model key (e.g., 'openai:gpt-4o-mini').
 * @returns {{inputTokens: number, outputTokens: number, cost: number}} The calculated cost details.
 */
export function logTotalCost(usageData, providerModel = 'unknown') {
  if (!usageData) {
    console.warn('[CostTracker] No usage data provided to logTotalCost');
    return { inputTokens: 0, outputTokens: 0, cost: 0 };
  }

  const {
    inputTokens = 0,
    outputTokens = 0,
    cost = 0,
    rateUsed
  } = calculateCost(usageData.inputTokens || 0, usageData.outputTokens || 0, providerModel);

  console.log(`
    LLM API Usage (${providerModel}):
    Input Tokens: ${inputTokens}
    Output Tokens: ${outputTokens}
    Rate Used: ${rateUsed ? `Input: $${rateUsed.input}/M, Output: $${rateUsed.output}/M` : 'N/A (Unknown Model)'}
    Total Cost: $${cost.toFixed(6)}
  `);

  return { inputTokens, outputTokens, cost };
}